/**
 * tts.js
 * Full voice conversation mode with server-side Whisper STT + edge-tts TTS
 * Opens a full-screen overlay for continuous voice conversation.
 */

import { state } from './state.js?v=302';
import { showToast } from './ui.js?v=302';
import { elements } from './dom.js?v=302';

// Server endpoints (proxied through nginx on same origin)
function getTtsUrl() {
  return `${window.location.origin}/tts`;
}
function getSttUrl() {
  return `${window.location.origin}/stt`;
}

let voiceOverlay = null;
let currentAudio = null;
let isListening = false;
let mediaRecorder = null;
let audioChunks = [];
let micStream = null;
let analyser = null;
let silenceCheckInterval = null;
let animationFrameId = null;

// Voice conversation state
let voiceConversationActive = false;

// ─── Voice Conversation Overlay ─────────────────────────────

function createVoiceOverlay() {
  if (voiceOverlay) return voiceOverlay;

  voiceOverlay = document.createElement('div');
  voiceOverlay.id = 'voice-overlay';
  voiceOverlay.className = 'voice-overlay';
  voiceOverlay.innerHTML = `
    <div class="voice-overlay-backdrop"></div>
    <div class="voice-overlay-content">
      <div class="voice-avatar-container">
        <div class="voice-avatar-ring"></div>
        <div class="voice-avatar-ring ring-2"></div>
        <div class="voice-avatar-ring ring-3"></div>
        <img src="dist/eliza-avatar.webp" alt="Eliza" class="voice-avatar-img" />
      </div>
      <div class="voice-status-text" id="voice-status-text">Listening...</div>
      <div class="voice-transcript" id="voice-transcript"></div>
      <div class="voice-controls">
        <button class="voice-btn voice-done-btn" id="voice-done-btn" aria-label="Done speaking">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 2L11 13"></path>
            <path d="M22 2L15 22L11 13L2 9L22 2Z"></path>
          </svg>
          Send
        </button>
        <button class="voice-btn voice-close-btn" id="voice-close-btn" aria-label="Close voice mode">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
          Close
        </button>
      </div>
    </div>
  `;

  document.body.appendChild(voiceOverlay);

  // Wire up buttons
  document.getElementById('voice-done-btn').addEventListener('click', onDoneSpeaking);
  document.getElementById('voice-close-btn').addEventListener('click', closeVoiceConversation);

  return voiceOverlay;
}

function setVoiceStatus(text, mode) {
  const el = document.getElementById('voice-status-text');
  if (!el) return;
  el.textContent = text;

  // Update overlay mode class for styling
  const overlay = document.getElementById('voice-overlay');
  if (overlay) {
    overlay.classList.remove('mode-listening', 'mode-transcribing', 'mode-thinking', 'mode-speaking');
    if (mode) overlay.classList.add(`mode-${mode}`);
  }
}

function setVoiceTranscript(text) {
  const el = document.getElementById('voice-transcript');
  if (!el) return;
  el.textContent = text || '';
  el.style.display = text ? 'block' : 'none';
}

function showDoneButton(visible) {
  const btn = document.getElementById('voice-done-btn');
  if (btn) btn.style.display = visible ? 'flex' : 'none';
}

// ─── Voice Conversation Flow ────────────────────────────────

/**
 * Open the voice conversation overlay and start listening
 */
export function openVoiceConversation() {
  voiceConversationActive = true;
  state.voiceMode = true;

  const overlay = createVoiceOverlay();
  overlay.offsetHeight;
  overlay.classList.add('visible');
  setVoiceTranscript('');
  showDoneButton(true);

  startRecording();
}

/**
 * Close the voice conversation overlay completely
 */
function closeVoiceConversation() {
  voiceConversationActive = false;
  state.voiceMode = false;

  stopRecording(false); // Don't process, just stop
  stopSpeaking();

  if (voiceOverlay) {
    voiceOverlay.classList.remove('visible');
  }

  updateMicButton();
}

/**
 * User pressed "Done" - stop recording and send
 */
function onDoneSpeaking() {
  if (!isListening) return;
  showDoneButton(false);
  stopRecording(true); // Process the audio
}

// ─── Recording (MediaRecorder + Whisper STT) ────────────────

async function startRecording() {
  if (isListening) return;

  try {
    micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch (e) {
    showToast('Microphone access denied. Check browser permissions.', 'error');
    closeVoiceConversation();
    return;
  }

  isListening = true;
  audioChunks = [];
  setVoiceStatus('Listening...', 'listening');
  setVoiceTranscript('');
  showDoneButton(true);
  updateMicButton();

  // Determine best supported mime type
  const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
    ? 'audio/webm;codecs=opus'
    : MediaRecorder.isTypeSupported('audio/webm')
      ? 'audio/webm'
      : 'audio/mp4';

  mediaRecorder = new MediaRecorder(micStream, { mimeType });

  mediaRecorder.ondataavailable = (e) => {
    if (e.data.size > 0) audioChunks.push(e.data);
  };

  mediaRecorder.onstop = async () => {
    // Stop mic tracks
    if (micStream) { micStream.getTracks().forEach(t => t.stop()); micStream = null; }
    isListening = false;
    updateMicButton();

    // If we're not processing (e.g., user closed overlay), bail
    if (!mediaRecorder._shouldProcess) {
      return;
    }

    if (audioChunks.length === 0) {
      if (voiceConversationActive) {
        setVoiceStatus('No audio captured. Try again.', 'listening');
        setTimeout(() => startRecording(), 1000);
      }
      return;
    }

    // Transcribe
    setVoiceStatus('Transcribing...', 'transcribing');
    const audioBlob = new Blob(audioChunks, { type: mimeType });
    audioChunks = [];

    try {
      const response = await fetch(getSttUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: audioBlob,
      });

      if (!response.ok) throw new Error(`STT error: ${response.status}`);

      const result = await response.json();
      const text = (result.text || '').trim();

      if (!text) {
        setVoiceStatus('No speech detected. Try again.', 'listening');
        if (voiceConversationActive) setTimeout(() => startRecording(), 1000);
        return;
      }

      // Show what was heard
      setVoiceTranscript(`"${text}"`);
      setVoiceStatus('Thinking...', 'thinking');

      // Send as a chat message and wait for response
      await sendVoiceMessage(text);

    } catch (e) {
      console.error('STT error:', e);
      setVoiceStatus('Transcription failed. Try again.', 'listening');
      if (voiceConversationActive) setTimeout(() => startRecording(), 1500);
    }
  };

  mediaRecorder.start(250);
}

function stopRecording(shouldProcess) {
  if (mediaRecorder && mediaRecorder.state !== 'inactive') {
    mediaRecorder._shouldProcess = shouldProcess;
    mediaRecorder.stop();
  } else {
    if (micStream) { micStream.getTracks().forEach(t => t.stop()); micStream = null; }
    isListening = false;
    updateMicButton();
  }
}

// ─── Send Message & Get Response ────────────────────────────

async function sendVoiceMessage(text) {
  // Put the text in the input and send via the existing chat system
  // The streaming TTS hook in api2.js handles speaking during the stream
  if (elements.input) {
    elements.input.value = text;
  }

  try {
    const { sendMessage } = await import('./chat.js?v=302');
    await sendMessage();

    // If streaming TTS wasn't triggered (e.g., error), go back to listening
    if (voiceConversationActive && !streamingTts) {
      setTimeout(() => startRecording(), 1000);
    }

  } catch (e) {
    console.error('Voice send error:', e);
    setVoiceStatus('Failed to send. Try again.', 'listening');
    if (voiceConversationActive) setTimeout(() => startRecording(), 1500);
  }
}

// ─── Text-to-Speech (TTS) Output ────────────────────────────

function cleanTextForSpeech(text) {
  return text
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/#{1,6}\s/g, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '')
    .replace(/^---+$/gm, '')
    .replace(/^[\s]*[-*+]\s/gm, '')
    .replace(/^[\s]*\d+\.\s/gm, '')
    .replace(/^>\s?/gm, '')
    .replace(/<[^>]+>/g, '')
    .replace(/:[a-z_]+:/g, '')
    .replace(/\|[^|]*\|/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/**
 * Speak text in the voice overlay, then resume listening
 */
async function speakInOverlay(text) {
  const cleanText = cleanTextForSpeech(text);
  if (!cleanText) {
    if (voiceConversationActive) startRecording();
    return;
  }

  setVoiceStatus('Speaking...', 'speaking');
  setVoiceTranscript('');
  showDoneButton(false);

  try {
    const response = await fetch(getTtsUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: cleanText }),
    });

    if (!response.ok) throw new Error(`TTS error: ${response.status}`);

    const audioBlob = await response.blob();
    const audioUrl = URL.createObjectURL(audioBlob);

    currentAudio = new Audio(audioUrl);

    currentAudio.onended = () => {
      URL.revokeObjectURL(audioUrl);
      currentAudio = null;

      // After speaking, go back to listening
      if (voiceConversationActive) {
        startRecording();
      }
    };

    currentAudio.onerror = () => {
      URL.revokeObjectURL(audioUrl);
      currentAudio = null;
      if (voiceConversationActive) startRecording();
    };

    await currentAudio.play();

  } catch (e) {
    console.warn('TTS failed, falling back to browser:', e.message);
    if (window.speechSynthesis) {
      speakWithBrowser(cleanText, () => {
        if (voiceConversationActive) startRecording();
      });
    } else {
      if (voiceConversationActive) startRecording();
    }
  }
}

/**
 * Speak text (called from api2.js when voice mode is on but overlay isn't active)
 */
export async function speakText(text) {
  if (voiceConversationActive) {
    // Already handled by the overlay flow
    return;
  }

  // Fallback: basic speak without overlay (shouldn't normally happen)
  const cleanText = cleanTextForSpeech(text);
  if (!cleanText) return;

  try {
    const response = await fetch(getTtsUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: cleanText }),
    });

    if (!response.ok) throw new Error(`TTS error: ${response.status}`);
    const audioBlob = await response.blob();
    const audioUrl = URL.createObjectURL(audioBlob);
    currentAudio = new Audio(audioUrl);
    currentAudio.onended = () => { URL.revokeObjectURL(audioUrl); currentAudio = null; };
    await currentAudio.play();
  } catch (e) {
    if (window.speechSynthesis) speakWithBrowser(cleanText);
  }
}

function speakWithBrowser(text, onDone) {
  const synth = window.speechSynthesis;
  synth.cancel();

  const chunks = splitIntoChunks(text, 200);
  let idx = 0;

  function speakNext() {
    if (idx >= chunks.length) {
      if (onDone) onDone();
      return;
    }

    const utt = new SpeechSynthesisUtterance(chunks[idx]);

    const voices = synth.getVoices();
    const preferred = ['Samantha', 'Zira', 'Jenny', 'Google UK English Female', 'Google US English', 'Karen', 'Victoria', 'Ava'];
    for (const name of preferred) {
      const match = voices.find(v => v.name.includes(name) && v.lang.startsWith('en'));
      if (match) { utt.voice = match; break; }
    }

    utt.rate = 1.0;
    utt.pitch = 1.0;
    utt.onend = () => { idx++; speakNext(); };
    utt.onerror = () => { if (onDone) onDone(); };

    synth.speak(utt);
  }

  speakNext();
}

function splitIntoChunks(text, maxWords) {
  const sentences = text.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [text];
  const chunks = [];
  let current = '';

  for (const sentence of sentences) {
    const combined = current ? current + ' ' + sentence.trim() : sentence.trim();
    if (combined.split(/\s+/).length > maxWords && current) {
      chunks.push(current.trim());
      current = sentence.trim();
    } else {
      current = combined;
    }
  }
  if (current.trim()) chunks.push(current.trim());
  return chunks;
}

export function stopSpeaking() {
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.currentTime = 0;
    currentAudio = null;
  }
  if (window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
  // Clear streaming TTS queue
  if (streamingTts) {
    streamingTts.cancel();
    streamingTts = null;
  }
}

// ─── Streaming TTS (speak while response streams in) ────────

let streamingTts = null;

class StreamingTTSQueue {
  constructor(onAllDone) {
    this.buffer = '';
    this.queue = [];       // audio URLs queued for playback
    this.playing = false;
    this.fetching = false;
    this.cancelled = false;
    this.done = false;     // no more text coming
    this.onAllDone = onAllDone;
    this.pendingFetches = 0;
  }

  /** Feed a delta chunk of text */
  feed(delta) {
    if (this.cancelled) return;
    this.buffer += delta;
    this._tryExtractSentence();
  }

  /** Signal that streaming is complete - flush remaining buffer */
  finish() {
    if (this.cancelled) return;
    this.done = true;
    const remaining = cleanTextForSpeech(this.buffer.trim());
    this.buffer = '';
    if (remaining) {
      this._fetchAndQueue(remaining);
    } else if (this.pendingFetches === 0 && !this.playing) {
      this._allDone();
    }
  }

  cancel() {
    this.cancelled = true;
    this.buffer = '';
    this.queue = [];
  }

  _tryExtractSentence() {
    // Smarter sentence boundary detection:
    // Look for . ! ? followed by a space and uppercase letter, or end of line.
    // Avoids splitting on abbreviations (Dr. Mr. e.g.), URLs, numbers (2.23).
    const match = this.buffer.match(/^([\s\S]*?[.!?])(?=\s+[A-Z"'\u201C\u201D([]|\s*\n|\s*$)/);
    if (match) {
      const sentence = cleanTextForSpeech(match[1].trim());
      this.buffer = this.buffer.slice(match[0].length);
      if (sentence) {
        this._fetchAndQueue(sentence);
      }
      this._tryExtractSentence();
    }
  }

  async _fetchAndQueue(text) {
    if (this.cancelled) return;
    this.pendingFetches++;

    try {
      const response = await fetch(getTtsUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });

      if (this.cancelled) { this.pendingFetches--; return; }
      if (!response.ok) { this.pendingFetches--; return; }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      this.queue.push(audioUrl);
      this.pendingFetches--;
      this._playNext();
    } catch (e) {
      this.pendingFetches--;
      console.warn('Streaming TTS fetch error:', e.message);
    }
  }

  _playNext() {
    if (this.cancelled || this.playing || this.queue.length === 0) {
      // Check if everything is done
      if (!this.playing && this.queue.length === 0 && this.done && this.pendingFetches === 0) {
        this._allDone();
      }
      return;
    }

    this.playing = true;
    const url = this.queue.shift();
    currentAudio = new Audio(url);

    currentAudio.onended = () => {
      URL.revokeObjectURL(url);
      currentAudio = null;
      this.playing = false;
      this._playNext();
    };

    currentAudio.onerror = () => {
      URL.revokeObjectURL(url);
      currentAudio = null;
      this.playing = false;
      this._playNext();
    };

    currentAudio.play().catch(() => {
      this.playing = false;
      this._playNext();
    });
  }

  _allDone() {
    if (this.onAllDone && !this.cancelled) this.onAllDone();
  }
}

/**
 * Start streaming TTS - call this at the beginning of a response stream.
 * Returns an object with feed(delta) and finish() methods.
 */
export function startStreamingTTS() {
  // Cancel any existing streaming TTS
  if (streamingTts) {
    streamingTts.cancel();
  }

  if (voiceConversationActive) {
    setVoiceStatus('Speaking...', 'speaking');
    setVoiceTranscript('');
    showDoneButton(false);
  }

  streamingTts = new StreamingTTSQueue(() => {
    streamingTts = null;
    // After all audio finishes, resume listening if in voice conversation
    if (voiceConversationActive) {
      startRecording();
    }
  });

  return streamingTts;
}

// ─── Voice Mode Toggle (mic button) ────────────────────────

export function toggleVoiceMode() {
  if (voiceConversationActive) {
    closeVoiceConversation();
  } else {
    openVoiceConversation();
  }
}

export function updateMicButton() {
  const btn = document.getElementById('voice-toggle-btn');
  if (!btn) return;

  if (voiceConversationActive) {
    btn.classList.add('active');
    if (isListening) {
      btn.classList.add('recording');
      btn.title = 'Voice conversation active (click to close)';
    } else {
      btn.classList.remove('recording');
      btn.title = 'Voice conversation active (click to close)';
    }
  } else {
    btn.classList.remove('active', 'recording');
    btn.title = 'Start voice conversation';
  }
}
